import { faExternalLink } from '@fortawesome/pro-regular-svg-icons';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DonationForm } from '@feathr/blackbox';
import {
  AlertV2 as Alert,
  Button,
  CodeBlock,
  CopyToClipboardButton,
  EAlertV2Type,
  Icon,
  Text,
  Well,
} from '@feathr/components';
import { handleOpenSupportChat } from '@feathr/extender/App/Page/PrimaryNav/HelpCard/HelpCard';
import type { IRedirectDomainOption } from '@feathr/extender/components/RedirectDomainSelect';
import ContentServingDomainSelect from '@feathr/extender/components/RedirectDomainSelect/ContentServingDomainSelect';
import {
  getDonationFormHeadScript,
  getInlineDonationFormDiv,
  getInlineDonationFormHeadAndInit,
  getModalDonationFormHash,
  getModalDonationFormLinkExample,
  useLocalUrl,
} from '@feathr/extender/state';

import type { EmbedType } from '../DonationFormEmbedModal';

import * as styles from './EmbedFlow.css';

interface IEmbedFlowProps {
  donationForm: DonationForm;
  redirectDomainId: string | undefined;
  contentDomain: string | undefined;
  isCodeReady: boolean;
  embedType: EmbedType;
  showDomainSelect: boolean;
  onSelectDomain: (option: IRedirectDomainOption) => void;
  onGenerateCode: () => Promise<void>;
  onCodeCopied?: (snippetId: string) => void;
}

function EmbedFlow({
  donationForm,
  redirectDomainId,
  contentDomain,
  isCodeReady,
  embedType,
  showDomainSelect,
  onSelectDomain,
  onGenerateCode,
  onCodeCopied,
}: Readonly<IEmbedFlowProps>): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();

  const showAlert = embedType === 'inline';
  const formId = donationForm.id;

  return (
    <div className={styles.embedContent}>
      <div className={styles.domainSection}>
        {/* Alert for inline embed */}
        {showAlert && (
          <Alert
            description={t(
              "Donation forms can't be embedded in email templates even if you copy the code. They're built for landing pages and websites only.",
            )}
            title={t("Won't work in emails")}
            type={EAlertV2Type.warning}
          />
        )}

        <Text.DefaultRegular truncate={false}>
          {t(
            "For your form to load correctly, you'll need to use a domain you've authorized in Feathr to serve this content.",
          )}{' '}
          <Button
            href={localUrl('/settings/account/domains')}
            suffix={<Icon icon={faExternalLink} />}
            target={'_blank'}
            type={'link'}
          >
            {t('View authorized domains')}
          </Button>
        </Text.DefaultRegular>
      </div>

      {showDomainSelect ? (
        <ContentServingDomainSelect
          label={t('Content serving domain')}
          onSelectSingle={onSelectDomain}
          selectedDomainId={redirectDomainId}
        />
      ) : (
        <Well label={t('Content serving domain')} layout={'vertical'}>
          {contentDomain}
        </Well>
      )}

      {!isCodeReady && (
        <Button disabled={!redirectDomainId} onClick={onGenerateCode} type={'primary'}>
          {t('Generate code')}
        </Button>
      )}

      {isCodeReady && contentDomain && (
        <>
          {/* Inline flow shows 2 blocks: head script, then div */}
          {embedType === 'inline' && (
            <>
              <Text.DefaultRegular className={styles.description}>
                {t('1. Add this to the <head> of your site.')}
              </Text.DefaultRegular>
              <div className={styles.codeBlockWrapper}>
                <CodeBlock
                  code={getInlineDonationFormHeadAndInit(contentDomain)}
                  language={'markup'}
                />
              </div>
              <CopyToClipboardButton
                className={styles.copyButton}
                name={'copy-head-script'}
                onMouseDown={(): void => {
                  onCodeCopied?.('inline-head-script');
                }}
                t={t}
                text={getInlineDonationFormHeadAndInit(contentDomain)}
                type={'secondary'}
              />

              <Text.DefaultRegular className={styles.description}>
                {t('2. Add a div where you want the form to appear.')}
              </Text.DefaultRegular>
              <div className={styles.codeBlockWrapper}>
                <CodeBlock code={getInlineDonationFormDiv(formId)} language={'markup'} />
              </div>
              <CopyToClipboardButton
                className={styles.copyButton}
                name={'copy-div'}
                onMouseDown={(): void => {
                  onCodeCopied?.('inline-div');
                }}
                t={t}
                text={getInlineDonationFormDiv(formId)}
                type={'secondary'}
              />
            </>
          )}

          {/* Popup flow shows everything on one page */}
          {embedType === 'popup' && (
            <>
              <Text.DefaultRegular className={styles.description}>
                {'1. Add this to the <head> of your site:'}
              </Text.DefaultRegular>
              <div className={styles.codeBlockWrapper}>
                <CodeBlock code={getDonationFormHeadScript(contentDomain)} language={'markup'} />
              </div>
              <CopyToClipboardButton
                className={styles.copyButton}
                name={'copy-head-script'}
                onMouseDown={(): void => {
                  onCodeCopied?.('popup-head');
                }}
                t={t}
                text={getDonationFormHeadScript(contentDomain)}
                type={'secondary'}
              />

              <Text.DefaultRegular className={styles.description}>
                {t('2. Add {{hash}} to any URL on your site, ie:', {
                  hash: getModalDonationFormHash(formId),
                })}
              </Text.DefaultRegular>
              <Text.DefaultRegular className={styles.urlDisplay}>
                {`https://${contentDomain}${getModalDonationFormHash(formId)}`}
              </Text.DefaultRegular>

              <Text.DefaultRegular className={styles.description}>
                {t('Alternatively, you can open the form with a button, ie:')}
              </Text.DefaultRegular>
              <div className={styles.codeBlockWrapper}>
                <CodeBlock code={getModalDonationFormLinkExample(formId)} language={'markup'} />
              </div>
              <CopyToClipboardButton
                className={styles.copyButton}
                name={'copy-link-example'}
                onMouseDown={(): void => {
                  onCodeCopied?.('popup-link');
                }}
                t={t}
                text={getModalDonationFormLinkExample(formId)}
                type={'secondary'}
              />
            </>
          )}
        </>
      )}

      <div className={styles.helpSection}>
        <Text.DefaultRegular>{t('Still need help?')}</Text.DefaultRegular>
        <div className={styles.helpLinks}>
          <Button
            href={
              'https://help.feathr.co/hc/en-us/articles/35989545493271-How-to-Create-a-Fundraising-Form'
            }
            target={'_blank'}
            type={'link'}
          >
            {t('Read our full guide')}
          </Button>
          {t(' or ')}
          <Button onClick={handleOpenSupportChat} type={'link'}>
            {t('chat with our support team')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EmbedFlow;
